/**
 * Activity renderer registry — maps each user-event type to a polished timeline
 * row descriptor (icon, accent, rich title, deep-link, optional badge). Mirrors
 * the backend Slack renderer registry (apps/api/src/user-events/slack-renderers.ts):
 * adding a new event type to the timeline is a one-entry change here.
 */
import React from 'react';
import { CheckCircle, Circle, Warning, Wrench } from '@phosphor-icons/react';
import type { UserEventType } from '@chatsdet/user-events';

/** Accent tints the icon chip. Maps to a fixed palette in ActivityRow. */
export type ActivityAccent = 'success' | 'info' | 'warning' | 'muted' | 'brand';

/**
 * A typed, discriminated deep-link target. Kept discriminated (not a raw string)
 * so RecentActivity can dispatch to TanStack's typed `navigate` without `as any`.
 */
export type ActivityLink =
  | { kind: 'flow'; workflowId: string }
  | { kind: 'runs' }
  | { kind: 'execution'; executionId: string };

export interface ActivityDescriptor {
  icon: React.ReactNode;
  accent: ActivityAccent;
  /** The action phrase rendered after the actor name. */
  title: React.ReactNode;
  /** Optional deep-link for the whole row. */
  link?: ActivityLink;
  /** Optional trailing pill (e.g. "12 flows"). */
  badge?: string;
}

type RenderInput = {
  metadata: Record<string, unknown>;
  actorName: string | null;
};

const ICON_SIZE = 18;

const str = (m: Record<string, unknown>, k: string): string | undefined =>
  typeof m[k] === 'string' ? (m[k] as string) : undefined;
const num = (m: Record<string, unknown>, k: string): number | undefined =>
  typeof m[k] === 'number' ? (m[k] as number) : undefined;

/** Bold entity name fragment used inside titles. */
function Entity({ children }: { children: React.ReactNode }) {
  return <span className="font-medium text-foreground">{children}</span>;
}

const flowName = (m: Record<string, unknown>) => str(m, 'name') ?? 'a flow';
const flowLink = (m: Record<string, unknown>): ActivityLink | undefined => {
  const workflowId = str(m, 'workflowId');
  return workflowId ? { kind: 'flow', workflowId } : undefined;
};

/**
 * The curated set of event types the timeline renders. Passed to `useOrgEvents`
 * as the server-side allowlist. Order here is irrelevant (events sort by time).
 */
export const TIMELINE_EVENT_TYPES: readonly UserEventType[] = [
  'workflow.approved',
  'test-suite.completed',
  'workflow.maintained',
];

const registry: Partial<Record<UserEventType, (input: RenderInput) => ActivityDescriptor>> = {
  'workflow.approved': ({ metadata }) => ({
    icon: <CheckCircle size={ICON_SIZE} />,
    accent: 'info',
    title: (
      <>
        approved <Entity>{flowName(metadata)}</Entity>
      </>
    ),
    link: flowLink(metadata),
  }),
  'test-suite.completed': ({ metadata }) => {
    const passed = num(metadata, 'passed') ?? 0;
    const total = num(metadata, 'total') ?? 0;
    const triggeredReason = str(metadata, 'triggeredReason');
    const ruleName = triggeredReason?.startsWith('automation:')
      ? triggeredReason.slice('automation:'.length)
      : undefined;
    const executionId = str(metadata, 'executionId');
    const status = str(metadata, 'status');

    let icon: React.ReactNode;
    let accent: ActivityAccent;
    if (status === 'completed') {
      icon = <CheckCircle size={ICON_SIZE} weight="fill" />;
      accent = 'success';
    } else if (status === 'completed_with_inconclusive') {
      icon = <Circle size={ICON_SIZE} />;
      accent = 'muted';
    } else {
      icon = <Warning size={ICON_SIZE} weight="fill" />;
      accent = 'warning';
    }

    return {
      icon,
      accent,
      title: ruleName ? (
        <>
          ran the <Entity>{ruleName}</Entity> suite
        </>
      ) : triggeredReason === 'release_qa' ? (
        <>ran a release QA suite</>
      ) : (
        <>completed a suite run</>
      ),
      link: executionId ? { kind: 'execution', executionId } : { kind: 'runs' },
      badge: `${passed}/${total} passed`,
    };
  },
  'workflow.maintained': ({ metadata }) => ({
    icon: <Wrench size={ICON_SIZE} />,
    accent: 'brand',
    title: (
      <>
        updated <Entity>{flowName(metadata)}</Entity> — {str(metadata, 'changeSummary')}
      </>
    ),
    link: flowLink(metadata),
  }),
};

/** Humanize an unmapped event type ("workflow.node-added" → "workflow node added"). */
function humanize(eventType: string): string {
  return eventType.replace(/[.\-_]/g, ' ');
}

/** Resolve a descriptor for any event, falling back to a neutral default. */
export function renderActivity(
  eventType: UserEventType,
  input: RenderInput
): ActivityDescriptor {
  const renderer = registry[eventType];
  if (renderer) return renderer(input);
  return {
    icon: <Circle size={ICON_SIZE} />,
    accent: 'muted',
    title: <>{humanize(eventType)}</>,
  };
}
